import React from 'react';
import { Link } from 'react-router-dom';

interface Ad {
  id: number;
  title: string;
  price: number;
  price_type: string;
  city: { name_ar: string } | null;
  category: { name_ar: string; slug: string } | null;
  subcategory: { name_ar: string } | null;
  images: { image_url: string; thumbnail_url: string | null; is_primary: boolean }[];
  views_count: number;
  favorites_count: number;
  is_promoted: boolean;
  created_at: string;
  user: { id: number; name: string; avatar: string | null; is_verified: boolean } | null;
}

interface Props {
  ad: Ad;
}

const AdCard: React.FC<Props> = ({ ad }) => {
  const primaryImage = ad.images?.find((img) => img.is_primary) || ad.images?.[0];
  const imageUrl = primaryImage?.thumbnail_url || primaryImage?.image_url;

  const priceLabel = () => {
    if (ad.price_type === 'negotiable') return 'قابل للتفاوض';
    if (ad.price_type === 'auction') return 'مزاد';
    return `${ad.price?.toLocaleString()} ر.س`;
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `منذ ${hours} ساعة`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `منذ ${days} يوم`;
    return `منذ ${Math.floor(days / 30)} شهر`;
  };

  return (
    <Link to={`/ads/${ad.id}`} className="block">
      <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition group">
        {/* الصورة */}
        <div className="relative h-48 bg-gray-100">
          {imageUrl ? (
            <img src={imageUrl} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <span className="text-4xl">📷</span>
            </div>
          )}

          {ad.is_promoted && (
            <span className="absolute top-2 right-2 bg-secondary-500 text-white text-xs px-2 py-1 rounded-md font-semibold">
              مميّز
            </span>
          )}

          {ad.category && (
            <span className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md">
              {ad.category.name_ar}
            </span>
          )}
        </div>

        {/* المحتوى */}
        <div className="p-3">
          <h3 className="font-semibold text-gray-800 text-sm truncate mb-1">{ad.title}</h3>

          <div className="flex items-center justify-between mb-2">
            <span className="text-primary-500 font-bold text-sm">{priceLabel()}</span>
            {ad.subcategory && (
              <span className="text-xs text-gray-400">{ad.subcategory.name_ar}</span>
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <span>📍</span>
              <span>{ad.city?.name_ar || 'غير محدد'}</span>
            </div>
            <span>{timeAgo(ad.created_at)}</span>
          </div>

          {/* المعلن */}
          {ad.user && (
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
              <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                {ad.user.avatar ? (
                  <img src={ad.user.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-primary-500 text-xs font-bold">{ad.user.name.charAt(0)}</span>
                )}
              </div>
              <span className="text-xs text-gray-500 truncate">{ad.user.name}</span>
              {ad.user.is_verified && <span className="text-blue-500 text-xs">✓</span>}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default AdCard;
